import { Injectable } from "@angular/core";
import { DetailedWatch, ListedWatch, Watch } from "../models/watch";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { WatchFilterService } from "./watch-filter.service";
import { WatchTransformationService } from "./watch-transformation.service";
import { Page } from "../data-types/page";
import { WatchListingDetail } from "../models/watch-listing-detail";

@Injectable({ providedIn: "root" })
export class WatchService {
  private baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private filterService: WatchFilterService,
    private transformationService: WatchTransformationService
  ) {}

  getWatches(
    filters?: any,
    page: number = 0,
    size: number = 10,
    includingInactives: boolean = false
  ): Observable<Page<Watch>> {
    let params = this.filterService
      .buildQueryParams(filters, size)
      .set("page", page.toString())
      .set("size", size.toString())
      .set("includingInactives", includingInactives.toString());

    return this.http.get<Page<Watch>>(`${this.baseUrl}/market/all-watches`, { params }).pipe(
      map((response) => {
        if (filters) {
          response.content = this.filterService.applyFilters(response.content, filters);
          response.content = this.transformationService.transformWatchesWithBrandName(response.content);
        }
        return response;
      }),
      catchError(this.handleError<Page<Watch>>("getWatches"))
    );
  }

  getWatchById(id: number): Observable<WatchListingDetail> {
    return this.http.get<WatchListingDetail>(`${this.baseUrl}/market/watch/${id}`);
  }

  getWatchDetailById(id: number): Observable<DetailedWatch | undefined> {
    // Use: `http://localhost:3000/market/watch/${id}` to test url
    return this.http.get<DetailedWatch>(`${this.baseUrl}/market/watch/${id}`);
  }

  createWatch(watch: Watch, images: string[] = []): Observable<Watch> {
    const payload = {
      watch,
      images,
    };
    return this.http
      .post<Watch>(`${this.baseUrl}/market/watch`, payload)
      .pipe(catchError(this.handleError<Watch>("createWatch")));
  }

  updateWatch(watch: Watch, images: string[] = []): Observable<Watch> {
    const payload = {
      watch,
      images,
    };

    return this.http
      .put<Watch>(`${this.baseUrl}/market/watch/${watch.id}`, payload)
      .pipe(catchError(this.handleError<Watch>("updateWatch")));
  }

  updateWatchById(id: number, data: WatchListingDetail): Observable<WatchListingDetail> {
    return this.http.put<WatchListingDetail>(`${this.baseUrl}/market/watch/${id}`, data);
  }

  deleteWatch(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/market/watch/${id}`)
      .pipe(catchError(this.handleError<void>("deleteWatch")));
  }

  getLatestPosts(limit: number = 8): Observable<ListedWatch[]> {
    const params = new HttpParams().set("limit", limit.toString());
    return (
      this.http
        // Use: `http://localhost:3000/market/latest-posts` to test url
        .get<any[]>(`${this.baseUrl}/market/latest-posts`, { params })
        .pipe(catchError(this.handleError<any[]>("getLatestPosts")))
    );
  }

  getListWatches(limit: number = 9): Observable<Page<ListedWatch[]>> {
    const params = new HttpParams().set("limit", limit.toString());
    return (
      this.http
        // Use: `http://localhost:3000/market/watches/paginated` to test url
        .get<Page<ListedWatch[]>>(`${this.baseUrl}/market/all-watch-listings`, { params })
        .pipe(catchError(this.handleError<Page<ListedWatch[]>>("all-watches")))
    );
  }
  getListWatchModels(limit: number = 9): Observable<Page<ListedWatch[]>> {
    const params = new HttpParams().set("limit", limit.toString());
    return (
      this.http
        // Use: `http://localhost:3000/market/watches/paginated` to test url
        .get<Page<ListedWatch[]>>(`${this.baseUrl}/market/all-watch-models`, { params })
        .pipe(catchError(this.handleError<Page<ListedWatch[]>>("all-watches")))
    );
  }

  getCaseMaterialById(id: number): Observable<{ id: number; material: string; isActive: boolean }> {
    return this.http
      .get<{ id: number; material: string; isActive: boolean }>(`${this.baseUrl}/market/case-material/${id}`)
      .pipe(catchError(this.handleError<{ id: number; material: string; isActive: boolean }>("getCaseMaterialById")));
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      return of(result as T);
    };
  }
}
